import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_MAIL_MESSAGES } from '@/api/endpoints';
import {
  MAIL_MESSAGE_QUERY_KEY,
  MAIL_MESSAGE_RECIPIENTS_QUERY_KEY,
} from '@/api/queryKeys';
import { toMailMessage, toMailRecipient } from '@/pages/Mail/mailMessages';
import type {
  MailMessage,
  MailMessageResponse,
  MailRecipientResponse,
} from '@/types/mail';
import { unwrapAxiosResponse } from '@/utils/axios';

export const useMailMessage = (messageId: string | null) => {
  const { data: mail, isFetching: isFetchingMail } = useQuery<MailMessage>({
    queryKey: [...MAIL_MESSAGE_QUERY_KEY, messageId],
    queryFn: () =>
      apiClient
        .get<MailMessageResponse>(`${API_MAIL_MESSAGES}/${messageId}`)
        .then(unwrapAxiosResponse)
        .then(toMailMessage),
    enabled: !!messageId,
  });

  const { data: separateRecipients, isFetching: isFetchingRecipients } =
    useQuery({
      queryKey: [...MAIL_MESSAGE_RECIPIENTS_QUERY_KEY, messageId],
      queryFn: () =>
        apiClient
          .get<MailRecipientResponse[]>(
            `${API_MAIL_MESSAGES}/${messageId}/recipients`,
          )
          .then(unwrapAxiosResponse)
          .then((recipients) => recipients.map(toMailRecipient)),
      enabled: !!mail?.loadRecipientsSeparately,
    });

  if (!mail) {
    return { mail: null, isLoading: isFetchingMail };
  }

  return {
    mail: separateRecipients
      ? { ...mail, recipients: separateRecipients }
      : mail,
    isLoading: isFetchingMail || isFetchingRecipients,
  };
};
